/**
 * QA 통신·페어링 점검 화면의 표시 규칙 — 모듈 판정 문구, 끊김 주기, 비상벨 문제 문구.
 * 값은 백엔드(siteQaInspect.go)가 현장 Prometheus·DB 에서 모은 것이다.
 */
import type {
  QaBellCheck,
  QaBellIssue,
  QaCctvModules,
  QaModuleHealth,
  QaModuleReport,
  QaModuleVerdict,
  QaPairingReport,
  QaPingCctv,
} from '../../services/SiteParkingQaService';

export const MODULE_VERDICT_LABEL: Record<QaModuleVerdict, string> = {
  dead: '무응답',
  no_cctv: 'CCTV 연결 없음',
  unstable: '불안정',
  no_modules: '모듈 미등록',
  no_data: '기록 없음',
  ok: '정상',
};

/** 화면에 놓는 순서(나쁜 것 먼저) — 백엔드 qaModRank 와 같다. */
export const MODULE_VERDICT_ORDER: QaModuleVerdict[] = ['dead', 'no_cctv', 'unstable', 'no_modules', 'no_data', 'ok'];

export const MODULE_VERDICT_COLOR: Record<QaModuleVerdict, 'error' | 'warning' | 'default' | 'success'> = {
  dead: 'error',
  no_cctv: 'error',
  unstable: 'warning',
  no_modules: 'default',
  no_data: 'default',
  ok: 'success',
};

/** 루트 127 · BLE 128 · 통로등 40. */
export function moduleLabel(m: Pick<QaModuleHealth, 'kind' | 'modId'>): string {
  if (m.kind === 'root') return `루트 ${m.modId}`;
  if (m.kind === 'ble') return `BLE ${m.modId}`;
  return `통로등 ${m.modId}`;
}

/** 분 → "45분" · "3시간" · "1.5시간" · "2일". */
export function formatMinutes(min: number): string {
  if (min < 60) return `${Math.max(1, Math.round(min))}분`;
  if (min < 1440) {
    const h = Math.round((min / 60) * 10) / 10;
    return `${h}시간`;
  }
  return `${Math.round((min / 1440) * 10) / 10}일`;
}

const pct = (r: number) => `${Math.round(r * 1000) / 10}%`;

/**
 * 모듈 한 줄 설명. 불안정이면 끊긴 횟수·평균 주기·한 번에 끊긴 시간, 무응답이면 기간 내내.
 * 기간 중 기록이 90% 미만이면(점검을 막 붙였거나 조명서버가 멈췄던 경우) 기록 길이를 덧붙인다.
 * 통로등은 aisleSilentMinutes 내내 무응답일 때만 무응답이고, 그보다 짧은 무응답은 정상으로 본다.
 */
export function describeModule(m: QaModuleHealth, windowMinutes: number, aisleSilentMinutes = 0): string {
  const cover = m.coverMinutes;
  const parts: string[] = [];
  const aisle = m.kind === 'aisle' && aisleSilentMinutes > 0;
  switch (m.verdict) {
    case 'ok':
      if (m.okRatio >= 0.999) parts.push('계속 응답');
      else if (aisle) parts.push(`짧은 무응답은 정상으로 봄(${formatMinutes(aisleSilentMinutes)} 기준) · 응답률 ${pct(m.okRatio)}`);
      else parts.push(`가끔 끊김은 정상으로 봄(응답률 95% 기준) · 응답률 ${pct(m.okRatio)}`);
      break;
    case 'dead':
      parts.push(
        aisle
          ? `최근 ${formatMinutes(aisleSilentMinutes)} 동안 한 번도 응답 없음`
          : `${formatMinutes(cover)} 동안 한 번도 응답 없음`,
      );
      break;
    case 'no_cctv':
      parts.push('조명서버가 CCTV 에 연결하지 못함');
      break;
    case 'no_data':
      return '이 기간 기록 없음';
    case 'unstable': {
      const down = (1 - m.okRatio) * cover;
      if (m.drops > 0) {
        parts.push(`${m.drops}번 끊김`);
        parts.push(`평균 ${formatMinutes(cover / m.drops)}마다`);
        parts.push(`한 번에 약 ${formatMinutes(down / m.drops)}`);
      } else {
        parts.push(`응답 없던 시간 ${formatMinutes(down)}`);
      }
      parts.push(`응답률 ${pct(m.okRatio)}`);
      break;
    }
    default:
      break;
  }
  if (m.verdict !== 'ok' && m.now === 0) parts.push('지금 무응답');
  if (cover < windowMinutes * 0.9) parts.push(`기록 ${formatMinutes(cover)}`);
  return parts.join(' · ');
}

/** 문제만 보기면 정상·기록 없음은 뺀다. level 이 비면 모든 층. */
export function filterModuleCctvs(cctvs: QaCctvModules[], problemsOnly: boolean, level: string): QaCctvModules[] {
  return cctvs.filter(
    (c) => (!problemsOnly || (c.verdict !== 'ok' && c.verdict !== 'no_data')) && (!level || c.level === level),
  );
}

/** 점검 결과에 있는 층 이름(정렬). */
export function moduleLevels(cctvs: QaCctvModules[]): string[] {
  return Array.from(new Set(cctvs.map((c) => c.level).filter(Boolean))).sort((a, b) => a.localeCompare(b, 'ko'));
}

/** 조명서버 점검이 이만큼(초) 멈춰 있으면 기록이 낡았다고 알린다 — 점검 주기 5분의 3배. */
export const SCAN_STALE_SECONDS = 15 * 60;

export const BELL_ISSUE_LABEL: Record<QaBellIssue, string> = {
  no_id: '벨 ID 없음',
  dup_id: '벨 ID 중복',
  no_cctv: '연결 CCTV 없음',
  missing_cctv: '없는 CCTV 에 연결',
  not_nearest: '더 가까운 CCTV 있음',
};

/** 비상벨 문제마다 현장에서 무슨 일이 생기는지. */
export function describeBell(b: QaBellCheck): string[] {
  return b.issues.map((i) => {
    switch (i) {
      case 'no_id':
        return '벨 ID(custom_id)가 없어 눌러도 어느 벨인지 모릅니다 — 신호가 센 CCTV 가 대신 깜빡입니다';
      case 'dup_id':
        return `벨 ID ${b.customId} 가 다른 벨에도 있습니다 — 한쪽을 누르면 다른 벨 자리가 깜빡일 수 있습니다`;
      case 'no_cctv':
        return '연결된 CCTV 가 없어 눌러도 경광등이 켜지지 않습니다';
      case 'missing_cctv':
        return `연결 CCTV '${b.cctv}' 가 현장 DB 에 없습니다(음소거 처리 포함)`;
      case 'not_nearest':
        return `연결 CCTV ${b.cctv}(거리 ${b.linkedDist}) 보다 가까운 CCTV 가 ${b.closer}대 — 가장 가까운 ${b.nearest}(거리 ${b.nearestDist}). 의도한 연결인지 확인`;
      default:
        return i;
    }
  });
}

export type QaInspectTone = 'error' | 'warning';

/**
 * 점검 탭(핑·모듈 통신·페어링)에서 맵에 색을 칠할 것 — 문제 있는 것만, 나머지는 회색.
 * 벨은 맵 쪽 이름과 벨 ID(customId) 중 무엇으로 맞춰질지 몰라 둘 다 키로 둔다.
 */
export interface QaInspectMarks {
  cctvs: Map<string, QaInspectTone>;
  bells: Map<string, QaInspectTone>;
  /** 통로등 등록이 꼬인 CCTV — 그 CCTV 의 통로등을 칠한다. */
  lightCctvs: Set<string>;
  /** 범례용 — 문제·확인 필요 수(CCTV + 벨, 벨은 하나로 센다). */
  counts: Record<QaInspectTone, number>;
}

const emptyMarks = (): QaInspectMarks => ({
  cctvs: new Map(),
  bells: new Map(),
  lightCctvs: new Set(),
  counts: { error: 0, warning: 0 },
});

/** CCTV 표시를 마치고 수를 센다. */
function countCctvs(m: QaInspectMarks): QaInspectMarks {
  m.cctvs.forEach((t) => {
    m.counts[t] += 1;
  });
  return m;
}

/** 같은 것에 문제가 여럿이면 더 나쁜 쪽(error)을 남긴다. */
function mark(map: Map<string, QaInspectTone>, key: string, tone: QaInspectTone) {
  if (!key) return;
  if (tone === 'error' || !map.has(key)) map.set(key, tone);
}

/** 핑 — 응답 없는 CCTV 만 문제. */
export function pingMarks(cctvs: QaPingCctv[]): QaInspectMarks {
  const m = emptyMarks();
  cctvs.forEach((c) => {
    if (!c.alive) mark(m.cctvs, c.name, 'error');
  });
  return countCctvs(m);
}

/** 모듈 통신 — 무응답·CCTV 연결 없음은 문제, 불안정은 확인 필요. 모듈 미등록·기록 없음은 칠하지 않는다. */
export function moduleMarks(report: Pick<QaModuleReport, 'cctvs'>): QaInspectMarks {
  const m = emptyMarks();
  report.cctvs.forEach((c) => {
    if (c.verdict === 'dead' || c.verdict === 'no_cctv') mark(m.cctvs, c.name, 'error');
    else if (c.verdict === 'unstable') mark(m.cctvs, c.name, 'warning');
  });
  return countCctvs(m);
}

/**
 * 페어링 — 벨은 ID 없음·중복·CCTV 없음·없는 CCTV 가 문제, "더 가까운 CCTV 있음" 만이면 확인 필요.
 * CCTV 는 통로등이 하나도 없으면 문제, 같은 통로등(MAC)이 여러 CCTV 에 있으면 확인 필요(그 통로등도 칠한다).
 */
export function pairingMarks(report: Pick<QaPairingReport, 'bells' | 'noAisle' | 'dupMac'>): QaInspectMarks {
  const m = emptyMarks();
  report.bells.forEach((b) => {
    const tone: QaInspectTone = b.issues.every((i) => i === 'not_nearest') ? 'warning' : 'error';
    mark(m.bells, b.name, tone);
    mark(m.bells, b.customId, tone);
    m.counts[tone] += 1;
  });
  report.noAisle.forEach((n) => mark(m.cctvs, n, 'error'));
  report.dupMac.forEach((d) =>
    d.cctvs.forEach((n) => {
      mark(m.cctvs, n, 'warning');
      m.lightCctvs.add(n);
    }),
  );
  return countCctvs(m);
}
